// My Rich Aunt organization + donor-profile helpers.
export const MRA_EIN = "99-4877688";
export const ORG_NAME = "My Rich Aunt";
export const ORG_TAGLINE = "A 501(c)(3) non-profit organization";
export const ORG_ADDRESS = "Direct grants to verified college students";
export const NO_GOODS_STATEMENT =
  "My Rich Aunt did not provide any goods or services in exchange for this gift. Your donation is tax-deductible to the fullest extent allowed by law.";

const PROFILE_KEY = "mra_donor_profile_v1";

const safeParse = (raw) => {
  try { return JSON.parse(raw); } catch (e) { return null; }
};

export const loadProfile = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PROFILE_KEY);
    return raw ? safeParse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const saveProfile = (profile) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch (e) { /* noop */ }
};

export const clearProfile = () => {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(PROFILE_KEY); } catch (e) { /* noop */ }
};

export const hasProfile = () => loadProfile() !== null;

// Deterministic-ish receipt id.
export const generateReceiptId = () => {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `MRA-${ymd}-${rand}`;
};

const formatDate = (d) => {
  const opts = { year: "numeric", month: "long", day: "numeric" };
  return d.toLocaleDateString("en-US", opts);
};

const fmtMoney = (n) => Number.isInteger(n) ? `$${n}.00` : `$${Number(n).toFixed(2)}`;

// Build a Receipt object from donation + donor data.
export const buildReceipt = ({
  donation, // { amount, boost, boostAmount, total }
  donor,    // { fullName, email, phone, street, city, state, zip, undergrad?, grad? }
  paymentLast4,
  campaignName, // "Maya B." or "My Rich Aunt Fund"
}) => {
  const now = new Date();
  return {
    id: generateReceiptId(),
    date: formatDate(now),
    orgName: ORG_NAME,
    orgTagline: ORG_TAGLINE,
    ein: MRA_EIN,
    donor: donor || null,
    donation: {
      base: donation.amount,
      boost: donation.boost ? donation.boostAmount : 0,
      total: donation.total,
    },
    campaignName,
    paymentLast4,
    statement: NO_GOODS_STATEMENT,
  };
};

// Renders a stand-alone printable HTML page for the receipt.
export const renderReceiptHTML = (r) => `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Donation Receipt · ${r.orgName}</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body {
    margin: 0; padding: 48px 24px; background: #F9F6F0; color: #1A1714;
    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px; line-height: 1.55;
  }
  .sheet {
    max-width: 640px; margin: 0 auto; background: #FFFFFF;
    border: 1px solid #E5DED0; border-radius: 12px; overflow: hidden;
  }
  .head {
    background: #FD6106; color: #F9F6F0; padding: 36px 40px;
    position: relative; overflow: hidden;
  }
  .head::after { content: ""; position: absolute; right: -60px; top: -60px; width: 180px; height: 180px; border-radius: 50%; background: rgba(253,208,6,0.35); filter: blur(24px); }
  .head::before { content: ""; position: absolute; left: -40px; bottom: -40px; width: 140px; height: 140px; border-radius: 50%; background: rgba(253,152,217,0.45); filter: blur(24px); }
  .head h1 { font-family: 'Cormorant Garamond', 'Georgia', serif; font-size: 40px; font-weight: 500; margin: 0; line-height: 1; position: relative; }
  .head p { margin: 6px 0 0; opacity: .9; position: relative; }
  .body { padding: 32px 40px; }
  .row { display: flex; justify-content: space-between; align-items: baseline; padding: 12px 0; border-bottom: 1px solid #EFEAE1; }
  .row:last-child { border-bottom: 0; }
  .row .label { color: #57503F; font-size: 12px; text-transform: uppercase; letter-spacing: .12em; }
  .row .value { color: #1A1714; font-weight: 600; font-size: 14px; text-align: right; }
  .amount { font-family: 'Cormorant Garamond', 'Georgia', serif; font-size: 48px; color: #FD6106; margin: 24px 0 8px; line-height: 1; }
  .amount-caption { color: #57503F; font-size: 12px; text-transform: uppercase; letter-spacing: .18em; }
  .breakdown { background: #F9F6F0; border-radius: 8px; padding: 16px 20px; margin-top: 20px; }
  .breakdown .row { padding: 6px 0; border: 0; }
  .statement { margin-top: 28px; padding: 20px 22px; background: #FFF8E5; border-left: 4px solid #FDD006; border-radius: 6px; font-size: 13px; color: #1A1714; line-height: 1.6; }
  .foot { padding: 20px 40px 32px; border-top: 1px solid #EFEAE1; color: #57503F; font-size: 12px; display: flex; justify-content: space-between; }
  .foot .id { font-family: ui-monospace, Menlo, Consolas, monospace; }
  @media print {
    body { padding: 0; background: #fff; }
    .sheet { border: 0; border-radius: 0; box-shadow: none; }
  }
</style>
</head>
<body>
  <div class="sheet">
    <div class="head">
      <h1>${r.orgName}</h1>
      <p>${r.orgTagline}</p>
      <p><strong>EIN:</strong> ${r.ein}</p>
    </div>
    <div class="body">
      <div class="amount-caption">Donation received</div>
      <div class="amount">${fmtMoney(r.donation.total)}</div>

      <div class="breakdown">
        <div class="row"><span>Campaign contribution</span><span>${fmtMoney(r.donation.base)}</span></div>
        ${r.donation.boost > 0
          ? `<div class="row"><span>Overhead boost (5%)</span><span>${fmtMoney(r.donation.boost)}</span></div>`
          : ""}
        <div class="row" style="font-weight:700;color:#1A1714;"><span>Total charged</span><span>${fmtMoney(r.donation.total)}</span></div>
      </div>

      <div style="margin-top:28px;">
        <div class="row"><span class="label">Date</span><span class="value">${r.date}</span></div>
        <div class="row"><span class="label">Designation</span><span class="value">${r.campaignName}</span></div>
        <div class="row"><span class="label">Donor</span><span class="value">${r.donor ? escapeHtml(r.donor.fullName) : "Anonymous"}</span></div>
        ${r.donor ? `<div class="row"><span class="label">Email</span><span class="value">${escapeHtml(r.donor.email)}</span></div>` : ""}
        ${r.donor ? `<div class="row"><span class="label">Address</span><span class="value">${escapeHtml(r.donor.street)}, ${escapeHtml(r.donor.city)}, ${escapeHtml(r.donor.state)} ${escapeHtml(r.donor.zip)}</span></div>` : ""}
        <div class="row"><span class="label">Payment</span><span class="value">Card ending in •••• ${r.paymentLast4 || "----"}</span></div>
      </div>

      <div class="statement">${r.statement}</div>
    </div>
    <div class="foot">
      <span>Receipt <span class="id">${r.id}</span></span>
      <span>Thank you.</span>
    </div>
  </div>
</body>
</html>`;

// Small HTML escaper for inserting user-provided values into the receipt template.
const escapeHtml = (s) => String(s ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

// Trigger a browser download for the receipt.
export const downloadReceipt = (receipt) => {
  if (typeof document === "undefined") return;
  const html = renderReceiptHTML(receipt);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${receipt.id}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Opens the receipt in a new tab for easy printing.
export const openReceipt = (receipt) => {
  if (typeof window === "undefined") return;
  const html = renderReceiptHTML(receipt);
  const w = window.open("", "_blank", "noopener,noreferrer");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
};

export { fmtMoney };

// Empty donor form values.
export const emptyDonor = () => ({
  fullName: "", email: "", phone: "",
  street: "", city: "", state: "", zip: "",
  undergrad: "", grad: "",
});

// Empty payment form values.
export const emptyPayment = () => ({ card: "", expiry: "", cvc: "" });

// Validate donor + payment. Returns errors object (empty if valid).
export const validateDonorForm = (donor, payment) => {
  const errors = {};
  if (!donor.fullName || !donor.fullName.trim()) errors.fullName = "Required";
  if (!donor.email || !/^\S+@\S+\.\S+$/.test(donor.email)) errors.email = "Enter a valid email";
  if (!donor.phone || !donor.phone.trim()) errors.phone = "Required";
  if (!donor.street || !donor.street.trim()) errors.street = "Required";
  if (!donor.city || !donor.city.trim()) errors.city = "Required";
  if (!donor.state) errors.state = "Required";
  if (!/^\d{5}(-\d{4})?$/.test((donor.zip || "").trim())) errors.zip = "5-digit ZIP";
  const rawCard = (payment.card || "").replace(/\s/g, "");
  if (rawCard.length < 12) errors.card = "Enter card number";
  if (!/^\d{2}\/\d{2}$/.test(payment.expiry || "")) errors.expiry = "MM/YY";
  if (!/^\d{3,4}$/.test(payment.cvc || "")) errors.cvc = "3–4 digits";
  return errors;
};
